import {
  Organisation,
  OrgSiteListEntry,
  SimplifiedSdmMatchConfig,
} from "@inrange/building-manager-api-client/models-organisation";
import "leaflet/dist/leaflet.css";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SimpleButton } from "../../exports";
import { fractionalCurrencySymbol } from "../../formatting/currency-utils";
import { formatUnitsToNearestTen } from "../../formatting/formatKiloUnits";
import { RowNoDefaults } from "../layout/Flex";
import { siteCanSell } from "../marketplace/marketplace-utils";
import "./map.css";
import {
  ForceWhiteText,
  MARKETPLACE_COLOR_PURPLE,
  PopupSubHeading,
  PopupText,
  PopupTextHighlighted,
  PopupTextLine,
} from "./marketplace-map-styles";
import RelatedOwnershipLine from "./RelatedOwnershipLine";

const MySiteSellExistingSellsMarker: React.FC<{
  org: Organisation;
  site: OrgSiteListEntry;
  existingBuys: SimplifiedSdmMatchConfig[];
  orgSitesById: Record<string, OrgSiteListEntry>;
}> = ({ org, site, existingBuys, orgSitesById }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlSpillSiteId = params.get("spillSiteId") || undefined;

  const sdmMatches = "sdmMatches" in site ? site.sdmMatches : [];
  const numNetworkExportSites =
    sdmMatches.filter((match) => match.sellerId === site.id).length +
    (site.energyFlowAnnual.exported > 0 ? 1 : 0);

  const totalExportableEnergy =
    site.energyFlowAnnual.networkExport +
    site.energyFlowAnnual.exported +
    (site.hasAllZeroExportLimit ? site.energyFlowAnnual.curtailed : 0);

  return (
    <div>
      <PopupText
        $fontWeight="0"
        $clickable={true}
        onClick={() => {
          navigate(`/org/${org.id}/site/${site.id}`);
        }}
      >
        {site.name}
      </PopupText>
      <PopupSubHeading $color={MARKETPLACE_COLOR_PURPLE}>
        Buying from {existingBuys.length} of your sites
      </PopupSubHeading>
      {existingBuys.map((buy) => (
        <PopupTextLine key={buy.sellerId}>
          <PopupTextHighlighted>
            {formatUnitsToNearestTen(buy.volume, "kWh")}
          </PopupTextHighlighted>{" "}
          / year @{" "}
          <PopupTextHighlighted>
            {(buy.tariff * 100).toFixed(2)}
            {fractionalCurrencySymbol(site.currencyCode)} / kWh
          </PopupTextHighlighted>{" "}
          from{" "}
          <PopupTextHighlighted>
            {orgSitesById[buy.sellerId].name}
          </PopupTextHighlighted>
        </PopupTextLine>
      ))}
      <PopupSubHeading $color={MARKETPLACE_COLOR_PURPLE}>
        Remaining energy to buy
      </PopupSubHeading>
      <PopupTextLine>
        <PopupTextHighlighted>
          {formatUnitsToNearestTen(site.energyFlowAnnual.gridImport, "kWh")}
        </PopupTextHighlighted>{" "}
        / year
      </PopupTextLine>
      <PopupSubHeading $color={MARKETPLACE_COLOR_PURPLE}>
        Total exportable energy
      </PopupSubHeading>
      <PopupTextLine>
        <PopupTextHighlighted>
          {formatUnitsToNearestTen(totalExportableEnergy, "kWh")}
        </PopupTextHighlighted>{" "}
        / year
      </PopupTextLine>
      <PopupTextLine>
        Selling to{" "}
        <PopupTextHighlighted>{numNetworkExportSites}</PopupTextHighlighted>{" "}
        network site{numNetworkExportSites === 1 ? "" : "s"}
      </PopupTextLine>
      <RelatedOwnershipLine org={org} site={site} />
      {siteCanSell(org, site) && (
        <ForceWhiteText>
          <RowNoDefaults $justifyContent="center" $margin="15px 0">
            <SimpleButton
              fontWeight="400"
              label={
                site.hasAllZeroExportLimit ? "Get wired offer" : "Get an offer"
              }
              width={"fit-content"}
              padding="16px 48px"
              to={`/org/${org.id}/marketplace?siteId=${site.id}&offerType=sell${urlSpillSiteId ? `&spillSiteId=${urlSpillSiteId}` : ""}`}
              background={"#a2a6ff"}
            />
          </RowNoDefaults>
        </ForceWhiteText>
      )}
    </div>
  );
};

export default MySiteSellExistingSellsMarker;
